import React, { useState, useMemo, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Modal, Input, Checkbox, Button, Empty, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { getCategoriesAdmin, getAllTools, getAdminTab } from '../../graphql/queries';
import { addTabItem } from '../../graphql/mutation';
import { showSnackBar } from '../../components/SnackBar/SnackBar';

// Bigger picker: search + scrollable multi-select list, so admins can add
// several categories/tools to a tab in one go instead of one at a time.
const AddContentModal = ({ open, tabId, contentType, onClose, onAdded }) => {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const { data: categoriesData, loading: categoriesLoading } = useQuery(getCategoriesAdmin, {
    skip: !open || contentType !== 'CATEGORY',
  });
  const { data: toolsData, loading: toolsLoading } = useQuery(getAllTools, {
    variables: { pageSize: 1000, page: 0 },
    skip: !open || contentType !== 'TOOL',
  });
  const { data: tabData, loading: tabLoading } = useQuery(getAdminTab, {
    variables: { id: tabId },
    skip: !open || !tabId,
    fetchPolicy: 'network-only',
  });

  const [addTabItemMutation] = useMutation(addTabItem);

  const loading = contentType === 'CATEGORY' ? categoriesLoading : toolsLoading;
  const allOptions = useMemo(
    () => (contentType === 'CATEGORY' ? categoriesData?.getCategoriesAdmin || [] : toolsData?.getAllTools?.tools || []),
    [contentType, categoriesData, toolsData]
  );

  const existingIds = useMemo(() => {
    const items = tabData?.getAdminTab?.items || [];
    return new Set(items.map((i) => (contentType === 'CATEGORY' ? i.categoryId : i.toolId)).filter(Boolean));
  }, [tabData, contentType]);

  const availableOptions = useMemo(
    () => allOptions.filter((o) => !existingIds.has(o.id)),
    [allOptions, existingIds]
  );

  const filteredOptions = useMemo(
    () => availableOptions.filter((o) => o.name?.toLowerCase().includes(search.toLowerCase())),
    [availableOptions, search]
  );

  useEffect(() => {
    if (!open) {
      setSearch('');
      setSelectedIds([]);
    }
  }, [open]);

  const toggleId = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const handleSelectAllFiltered = () => {
    const filteredIds = filteredOptions.map((o) => o.id);
    const allSelected = filteredIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...filteredIds])));
    }
  };

  const handleSubmit = async () => {
    if (selectedIds.length === 0) return;
    try {
      setSubmitting(true);
      await Promise.all(
        selectedIds.map((id) =>
          addTabItemMutation({
            variables: {
              tabId,
              categoryId: contentType === 'CATEGORY' ? id : null,
              toolId: contentType === 'TOOL' ? id : null,
            },
          })
        )
      );
      showSnackBar(
        'success',
        'Success',
        `${selectedIds.length} ${contentType === 'CATEGORY' ? 'categor' + (selectedIds.length > 1 ? 'ies' : 'y') : 'tool' + (selectedIds.length > 1 ? 's' : '')} added to tab`
      );
      onAdded();
    } catch (error) {
      showSnackBar('error', 'Error', error.message || 'Failed to add content');
    } finally {
      setSubmitting(false);
    }
  };

  const allFilteredSelected = filteredOptions.length > 0 && filteredOptions.every((o) => selectedIds.includes(o.id));

  return (
    <Modal
      open={open}
      title={`Add ${contentType === 'CATEGORY' ? 'Categories' : 'Tools'} to Tab`}
      onCancel={onClose}
      width={640}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="submit" type="primary" loading={submitting} disabled={selectedIds.length === 0} onClick={handleSubmit}>
          Add {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
        </Button>,
      ]}
    >
      <Input
        placeholder={`Search ${contentType === 'CATEGORY' ? 'categories' : 'tools'}...`}
        prefix={<SearchOutlined />}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
        style={{ marginBottom: 12 }}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ color: '#888', fontSize: 13 }}>{filteredOptions.length} available</span>
        {filteredOptions.length > 0 && (
          <Checkbox checked={allFilteredSelected} onChange={handleSelectAllFiltered}>
            Select all
          </Checkbox>
        )}
      </div>

      <div className="add-content-list">
        {loading || tabLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : filteredOptions.length === 0 ? (
          <Empty description="No matching items" />
        ) : (
          filteredOptions.map((option) => (
            <div key={option.id} className="add-content-row" onClick={() => toggleId(option.id)}>
              <Checkbox checked={selectedIds.includes(option.id)} onClick={(e) => e.stopPropagation()} onChange={() => toggleId(option.id)} />
              <span className="add-content-row-name">{option.name}</span>
            </div>
          ))
        )}
      </div>
    </Modal>
  );
};

export default AddContentModal;
